import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Layout } from "@/components/Layout";
import { StageChip } from "@/components/StageChip";
import { formatINR, banksLabel, fmtDate, fmtLastConv } from "@/lib/format";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { ArrowLeft, Loader2, Eye, Download, Copy, Upload, MessageSquareWarning, Send, Archive, Check } from "lucide-react";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL;
const STATUS_TONE = { Received: "bg-[#F5B71D]/15 text-[#92600a]", Verified: "bg-[#159A50]/10 text-[#159A50]", "Needs re-upload": "bg-[#CA1212]/10 text-[#CA1212]", Partial: "bg-[#8B46FF]/10 text-[#8B46FF]" };

export default function PartnerCaseView() {
  const { recordId } = useParams();
  const navigate = useNavigate();
  const [c, setC] = useState(null);
  const [err, setErr] = useState(null);
  const [tab, setTab] = useState("documents");

  const load = async () => {
    try { setC((await api.get(`/partner/cases/${recordId}`)).data); setErr(null); }
    catch (e) { setErr(e?.response?.status === 403 ? "This case isn't part of your cases." : (e?.response?.data?.detail || "Could not load case")); }
  };
  useEffect(() => { load(); }, [recordId]); // eslint-disable-line

  if (err) return <Layout><button onClick={() => navigate("/partner")} className="text-sm text-[#6B7280] mb-3 flex items-center gap-1"><ArrowLeft className="w-4 h-4" /> Back</button><div className="rounded-2xl border border-[#CA1212]/30 bg-[#CA1212]/5 p-6 text-sm text-[#CA1212]" data-testid="partner-case-error">{err}</div></Layout>;
  if (!c) return <Layout><div className="flex justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-[#FF4802]" /></div></Layout>;

  return (
    <Layout>
      <button onClick={() => navigate("/partner")} data-testid="partner-back" className="flex items-center gap-1.5 text-sm text-[#6B7280] hover:text-[#333333] mb-3"><ArrowLeft className="w-4 h-4" /> Back</button>
      <div className="rounded-2xl border border-[#E2E2E2] bg-white p-5 shadow-sm mb-4" data-testid="partner-case-header">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <div className="flex items-center gap-2"><span className="font-mono text-[#E2521B] font-semibold text-sm">{c.case_id}</span><StageChip stage={c.stage} />{c.days_in_stage != null && <span className="text-xs text-[#6B7280]">{c.days_in_stage}d in stage</span>}</div>
            <h1 className="text-xl font-semibold text-[#333333] mt-1">{c.borrower || "—"}</h1>
            <div className="text-sm text-[#6B7280] mt-1">{banksLabel(c.banks) || "Bank TBD"} · {formatINR(c.loan_amount)}{(c.property_truva_name || c.property) && <> · {c.property_truva_name || c.property}</>}</div>
          </div>
          <div className="text-right text-sm">
            <div className="text-xs text-[#6B7280] uppercase tracking-wider">HL RM</div>
            <div className="font-medium text-[#333333]">{c.hl_rm || "—"}</div>
            {c.hl_rm_phone && <a href={`tel:${c.hl_rm_phone}`} className="text-[#FF4802] text-xs hover:underline" data-testid="partner-rm-phone">{c.hl_rm_phone}</a>}
          </div>
        </div>
      </div>

      <div className="flex gap-1 bg-[#E2E2E2]/50 p-1 rounded-xl w-fit mb-4">
        {[["documents", "Documents"], ["queries", `Bank queries${c.queries.length ? ` (${c.queries.length})` : ""}`], ["updates", "Updates"]].map(([k, l]) => (
          <button key={k} onClick={() => setTab(k)} data-testid={`partner-tab-${k}`} className={`px-4 py-1.5 text-sm rounded-lg ${tab === k ? "bg-white shadow text-[#333333] font-medium" : "text-[#6B7280]"}`}>{l}</button>
        ))}
      </div>

      {tab === "documents" && <Documents c={c} recordId={recordId} reload={load} />}
      {tab === "queries" && <Queries c={c} recordId={recordId} reload={load} />}
      {tab === "updates" && <Updates c={c} recordId={recordId} reload={load} />}
    </Layout>
  );
}

function copy(text, label = "Copied") { navigator.clipboard?.writeText(text); toast.success(label); }

function Documents({ c, recordId, reload }) {
  const [zip, setZip] = useState(null);
  const open = async (doc, mode) => {
    try {
      const r = await api.post(`/partner/cases/${recordId}/files/${encodeURIComponent(doc.id)}/link?mode=${mode}`);
      window.open(`${API}${r.data.url}${mode === "download" ? "?download=1" : ""}`, "_blank");
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not open file"); }
  };
  const downloadAll = async () => {
    try {
      const r = await api.post(`/partner/cases/${recordId}/download-all`);
      setZip({ job: r.data.job, status: "processing", n: r.data.n });
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not start download"); }
  };
  useEffect(() => {
    if (!zip || zip.status !== "processing") return undefined;
    const t = setInterval(async () => {
      const r = await api.get(`/partner/download-all/${zip.job}`);
      if (r.data.status !== "processing") { setZip(r.data); clearInterval(t); }
    }, 2000);
    return () => clearInterval(t);
  }, [zip]); // eslint-disable-line
  const reveal = (which, value) => { api.post(`/partner/cases/${recordId}/password-reveal`, { which }); copy(value, "Password copied"); };
  const total = c.groups.reduce((n, g) => n + g.docs.length, 0);
  return (
    <div className="space-y-4" data-testid="partner-documents">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="text-sm text-[#6B7280]">{total} {total === 1 ? "file" : "files"}{c.passwords.bank_statement && <> · Bank statement password: <button onClick={() => reveal("bank_statement", c.passwords.bank_statement)} className="font-mono text-[#333333] hover:text-[#FF4802]" data-testid="partner-pwd-bank">{c.passwords.bank_statement} <Copy className="w-3 h-3 inline" /></button></>}{c.passwords.salary_slip && <> · Salary slip password: <button onClick={() => reveal("salary_slip", c.passwords.salary_slip)} className="font-mono text-[#333333] hover:text-[#FF4802]" data-testid="partner-pwd-salary">{c.passwords.salary_slip} <Copy className="w-3 h-3 inline" /></button></>}</div>
        <div className="flex items-center gap-2">
          {zip?.status === "processing" && <span className="text-xs text-[#6B7280] flex items-center gap-1" data-testid="partner-zip-processing"><Loader2 className="w-3 h-3 animate-spin text-[#FF4802]" /> Processing… building {zip.n} files</span>}
          {zip?.status === "ready" && <a href={`${API}${zip.url}`} className="text-xs bg-[#159A50] text-white px-3 py-1.5 rounded-lg flex items-center gap-1" data-testid="partner-zip-ready"><Download className="w-3 h-3" /> {zip.name}</a>}
          {zip?.status === "error" && <span className="text-xs text-[#CA1212]">Zip failed — try again</span>}
          <button onClick={downloadAll} disabled={zip?.status === "processing" || total === 0} data-testid="partner-download-all" className="text-xs bg-[#222222] text-white px-3 py-1.5 rounded-lg flex items-center gap-1 disabled:opacity-50"><Archive className="w-3 h-3" /> Download all</button>
        </div>
      </div>

      {c.pending.length > 0 && (
        <div className="rounded-2xl border border-[#CA1212]/30 bg-[#CA1212]/5 p-3" data-testid="partner-pending">
          <div className="text-xs font-medium text-[#CA1212] mb-1">Still missing ({c.pending.length})</div>
          <div className="flex flex-wrap gap-1.5">{c.pending.map((p, i) => <span key={i} className="text-[11px] bg-white border border-[#CA1212]/30 rounded-full px-2 py-0.5 text-[#333333]">{p.label}<span className="text-[#6B7280]"> · {p.applicant}</span>{p.status !== "missing" && <span className="text-[#8B46FF]"> · {p.status === "partial" ? "partial" : "re-upload"}</span>}</span>)}</div>
        </div>
      )}

      {c.groups.map((g) => (
        <div key={g.slug} className="rounded-2xl border border-[#E2E2E2] bg-white shadow-sm overflow-hidden" data-testid={`partner-group-${g.slug}`}>
          <div className="px-4 py-2.5 text-sm font-medium text-[#333333] bg-[#F7F7F7] flex justify-between"><span>{g.label}</span><span className="text-[#6B7280] text-xs">{g.docs.length}</span></div>
          {g.docs.length === 0 && <div className="px-4 py-3 text-xs text-[#C5C4C4]">No files yet</div>}
          {g.docs.map((d) => (
            <div key={d.id} className="px-4 py-2.5 border-t border-[#F7F7F7] flex flex-wrap items-center justify-between gap-2" data-testid={`partner-doc-${d.id}`}>
              <div className="min-w-0">
                <div className="text-sm text-[#333333] truncate">{d.document_type || d.document}{d.period && <span className="text-[#6B7280]"> · {d.period}</span>}</div>
                <div className="text-[11px] text-[#6B7280] truncate">{d.file_name || d.original_name || (d.link ? "Link" : "")}{d.received_at && <> · {fmtDate(d.received_at)}</>}{d.truva_house && " · Truva House"}</div>
              </div>
              <div className="flex items-center gap-1.5">
                <span className={`text-[11px] px-2 py-0.5 rounded-full ${STATUS_TONE[d.status] || "bg-[#F7F7F7] text-[#6B7280]"}`}>{d.status}</span>
                {d.password && <button onClick={() => reveal(d.id, d.password)} className="text-[11px] font-mono border border-[#E2E2E2] rounded-md px-1.5 py-0.5 hover:border-[#FF4802]" title="Copy password" data-testid={`partner-doc-pwd-${d.id}`}>{d.password} <Copy className="w-3 h-3 inline" /></button>}
                {d.link && <a href={d.link} target="_blank" rel="noreferrer" className="text-[11px] text-[#FF4802] hover:underline">Open link</a>}
                {d.file_url && <>
                  <button onClick={() => open(d, "view")} className="p-1.5 rounded-md border border-[#E2E2E2] hover:border-[#FF4802] text-[#333333]" title="View" data-testid={`partner-doc-view-${d.id}`}><Eye className="w-3.5 h-3.5" /></button>
                  <button onClick={() => open(d, "download")} className="p-1.5 rounded-md border border-[#E2E2E2] hover:border-[#FF4802] text-[#333333]" title="Download" data-testid={`partner-doc-download-${d.id}`}><Download className="w-3.5 h-3.5" /></button>
                </>}
              </div>
            </div>
          ))}
        </div>
      ))}
      <UploadBox c={c} recordId={recordId} reload={reload} />
    </div>
  );
}

function UploadBox({ c, recordId, reload }) {
  const [type, setType] = useState(c.upload_types[0]);
  const [note, setNote] = useState("");
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (!file) return toast.error("Choose a file");
    if (type === "Other" && !note.trim()) return toast.error("Add a note for 'Other'");
    setBusy(true);
    const fd = new FormData(); fd.append("file", file); fd.append("document_type", type); fd.append("note", note);
    try { await api.post(`/partner/cases/${recordId}/upload`, fd, { headers: { "Content-Type": "multipart/form-data" } }); toast.success("Uploaded · the RM has been notified"); setFile(null); setNote(""); reload(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Upload failed"); }
    finally { setBusy(false); }
  };
  return (
    <div className="rounded-2xl border border-dashed border-[#FF4802]/40 bg-white p-4" data-testid="partner-upload">
      <div className="text-sm font-medium text-[#333333] flex items-center gap-2 mb-2"><Upload className="w-4 h-4 text-[#FF4802]" /> Upload a bank document</div>
      <div className="flex flex-wrap items-center gap-2">
        <select value={type} onChange={(e) => setType(e.target.value)} data-testid="partner-upload-type" className="text-sm border border-[#E2E2E2] rounded-lg px-2 py-1.5 bg-white">{c.upload_types.map((t) => <option key={t}>{t}</option>)}</select>
        <input type="file" onChange={(e) => setFile(e.target.files?.[0] || null)} data-testid="partner-upload-file" className="text-xs" />
        <Input value={note} onChange={(e) => setNote(e.target.value)} placeholder={type === "Other" ? "What is this? (required)" : "Note (optional)"} className="h-9 text-sm w-60 border-[#E2E2E2] rounded-lg" data-testid="partner-upload-note" />
        <button onClick={submit} disabled={busy} data-testid="partner-upload-submit" className="text-sm bg-[#FF4802] text-white px-4 py-1.5 rounded-lg disabled:opacity-50 flex items-center gap-1">{busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />} Upload</button>
      </div>
    </div>
  );
}

function Queries({ c, recordId, reload }) {
  const [text, setText] = useState("");
  const [docs, setDocs] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (!text.trim()) return toast.error("Write the bank's query");
    setBusy(true);
    try { await api.post(`/partner/cases/${recordId}/query`, { text, documents: docs.split(/[\n,]/).map((s) => s.trim()).filter(Boolean) }); toast.success("Query raised · the RM will see it in My Day"); setText(""); setDocs(""); reload(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };
  return (
    <div className="space-y-4" data-testid="partner-queries">
      <div className="rounded-2xl border border-[#E2E2E2] bg-white p-4 shadow-sm">
        <div className="text-sm font-medium text-[#333333] flex items-center gap-2 mb-2"><MessageSquareWarning className="w-4 h-4 text-[#FF4802]" /> Raise a query from the bank</div>
        <Textarea value={text} onChange={(e) => setText(e.target.value)} placeholder='e.g. "HDFC needs Form 16 for FY23 and a gap explanation letter"' className="text-sm border-[#E2E2E2] rounded-lg mb-2" data-testid="partner-query-text" />
        <Input value={docs} onChange={(e) => setDocs(e.target.value)} placeholder="Documents requested, comma-separated (each becomes a checklist item for the RM)" className="text-sm border-[#E2E2E2] rounded-lg mb-3" data-testid="partner-query-docs" />
        <button onClick={submit} disabled={busy} data-testid="partner-query-submit" className="text-sm bg-[#FF4802] text-white px-4 py-1.5 rounded-lg disabled:opacity-50 flex items-center gap-1"><Send className="w-3.5 h-3.5" /> Raise query</button>
      </div>
      {c.queries.map((q) => (
        <div key={q.id} className="rounded-2xl border border-[#E2E2E2] bg-white p-3.5 shadow-sm text-sm" data-testid={`partner-query-${q.id}`}>
          <div className="flex items-center justify-between gap-2"><span className="text-[#333333]">{q.text}</span><span className={`text-[11px] px-2 py-0.5 rounded-full whitespace-nowrap ${q.status === "shared" ? "bg-[#159A50]/10 text-[#159A50]" : "bg-[#F5B71D]/15 text-[#92600a]"}`}>{q.status === "shared" ? <><Check className="w-3 h-3 inline" /> Shared with you</> : "Waiting on Truva"}</span></div>
          {q.documents?.length > 0 && <div className="text-[11px] text-[#6B7280] mt-1">Requested: {q.documents.join(", ")}</div>}
          <div className="text-[11px] text-[#6B7280] mt-1">{q.by_name} · {fmtLastConv(q.created_at)}{q.shared_at && <> · shared {fmtLastConv(q.shared_at)}</>}</div>
        </div>
      ))}
    </div>
  );
}

function Updates({ c, recordId, reload }) {
  const [text, setText] = useState("");
  const [stage, setStage] = useState("");
  const [busy, setBusy] = useState(false);
  const post = async () => {
    if (!text.trim()) return toast.error("Write one line");
    setBusy(true);
    try { await api.post(`/partner/cases/${recordId}/update`, { text, suggested_stage: stage || null }); toast.success("Update posted"); setText(""); setStage(""); reload(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };
  return (
    <div className="space-y-4" data-testid="partner-updates">
      <div className="rounded-2xl border border-[#E2E2E2] bg-white p-4 shadow-sm">
        <div className="text-sm font-medium text-[#333333] mb-2">Post update</div>
        <Textarea value={text} onChange={(e) => setText(e.target.value)} placeholder='"Logged in at HDFC", "Valuation done", "Sanction expected Friday"' className="text-sm border-[#E2E2E2] rounded-lg mb-2" data-testid="partner-update-text" />
        <div className="flex flex-wrap items-center gap-2">
          <select value={stage} onChange={(e) => setStage(e.target.value)} data-testid="partner-update-stage" className="text-sm border border-[#E2E2E2] rounded-lg px-2 py-1.5 bg-white">
            <option value="">Suggest a stage change (optional)</option>
            {["Document Collection", "Bank Login", "Sanction", "Disbursement", "Part Disbursement", "Disbursed"].map((s) => <option key={s}>{s}</option>)}
          </select>
          <button onClick={post} disabled={busy} data-testid="partner-update-submit" className="text-sm bg-[#FF4802] text-white px-4 py-1.5 rounded-lg disabled:opacity-50 flex items-center gap-1"><Send className="w-3.5 h-3.5" /> Post update</button>
        </div>
        <p className="text-[11px] text-[#6B7280] mt-2">The RM confirms any stage change — nothing changes automatically.</p>
      </div>
      {c.partner_updates.map((u) => (
        <div key={u.id} className="rounded-2xl border border-[#E2E2E2] bg-white p-3.5 shadow-sm text-sm" data-testid={`partner-update-${u.id}`}>
          <div className="text-[#333333]">{u.text}{u.suggested_stage && <span className="text-[11px] ml-2 px-2 py-0.5 rounded-full bg-[#8B46FF]/10 text-[#8B46FF]">suggested: {u.suggested_stage}</span>}</div>
          <div className="text-[11px] text-[#6B7280] mt-1">{u.spoc_name} · {fmtLastConv(u.created_at)} · {u.acknowledged_at ? "Seen by Truva" : "Pending"}</div>
        </div>
      ))}
    </div>
  );
}
